/**
 * Les choses à faire : ranger, trier, compter.
 *
 * Une liste de tâches ne vaut que par l'ordre dans lequel elle se présente.
 * Trente lignes à plat, personne ne les lit ; les mêmes trente rangées en
 * « en retard / aujourd'hui / demain », on sait par quoi commencer.
 *
 * Deux partis pris tenus partout :
 *
 *  1. **Une échéance est un JOUR, pas un instant.** Une tâche prévue
 *     aujourd'hui ne bascule pas « en retard » à midi parce qu'elle portait
 *     une heure. Elle le devient demain. C'est ainsi qu'on y pense sur un
 *     chantier, et c'est ainsi que le module compte.
 *  2. **Tout est raisonné en heure LOCALE.** La journée qui compte est celle
 *     qu'on lit sur son écran, pas la journée UTC.
 */

export type StatutTache = 'a_faire' | 'en_cours' | 'fait';
export type PrioriteTache = 'basse' | 'normale' | 'haute' | 'urgente';

export const STATUTS: readonly StatutTache[] = ['a_faire', 'en_cours', 'fait'];
export const PRIORITES: readonly PrioriteTache[] = ['basse', 'normale', 'haute', 'urgente'];

export const LIBELLE_STATUT: Record<StatutTache, string> = {
  a_faire: 'À faire',
  en_cours: 'En cours',
  fait: 'Fait',
};

export const LIBELLE_PRIORITE: Record<PrioriteTache, string> = {
  basse: 'Basse',
  normale: 'Normale',
  haute: 'Haute',
  urgente: 'Urgente',
};

/** Du plus urgent au moins urgent : sert au tri, pas à l'affichage. */
const POIDS_PRIORITE: Record<PrioriteTache, number> = {
  urgente: 0,
  haute: 1,
  normale: 2,
  basse: 3,
};

export interface Tache {
  id: string;
  titre: string;
  description: string | null;
  statut: StatutTache;
  priorite: PrioriteTache;
  /** Date ISO, ou `null` quand rien n'est prévu. */
  echeance: string | null;
  assigneAId: string | null;
  assigneANom: string | null;
  /** `chantier` | `contact` | `devis` | `facture`, ou `null`. */
  entiteType: string | null;
  entiteId: string | null;
  entiteLibelle: string | null;
  createdAt: string;
  completeAt: string | null;
}

export type Tranche =
  | 'en_retard'
  | 'aujourdhui'
  | 'demain'
  | 'cette_semaine'
  | 'plus_tard'
  | 'sans_echeance'
  | 'fait';

/** L'ordre dans lequel les tranches se présentent à l'écran. */
export const ORDRE_TRANCHES: readonly Tranche[] = [
  'en_retard',
  'aujourdhui',
  'demain',
  'cette_semaine',
  'plus_tard',
  'sans_echeance',
  'fait',
];

export const LIBELLE_TRANCHE: Record<Tranche, string> = {
  en_retard: 'En retard',
  aujourdhui: "Aujourd'hui",
  demain: 'Demain',
  cette_semaine: 'Cette semaine',
  plus_tard: 'Plus tard',
  sans_echeance: 'Sans échéance',
  fait: 'Fait',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** La clé d'une journée locale, `AAAA-MM-JJ`. */
export function cleJour(d: Date): string {
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Une date de jour → l'instant à stocker.
 *
 * Midi, et non minuit : une échéance posée à minuit local bascule de journée
 * dès qu'on la relit sous un autre décalage — ou au changement d'heure. À midi,
 * douze heures de marge de chaque côté, la journée reste la bonne partout.
 */
export function instantEcheance(jour: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(jour.trim());
  if (!m) return null;
  const [y, mois, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mois - 1, d, 12, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return null;
  // Une date recomposée différente de celle demandée trahit un 31 février.
  if (date.getFullYear() !== y || date.getMonth() !== mois - 1 || date.getDate() !== d) return null;
  return date.toISOString();
}

/** L'inverse : l'instant stocké → le jour à afficher dans un champ date. */
export function jourEcheance(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : cleJour(d);
}

function dansLaSemaine(jour: string, aujourdhui: Date): boolean {
  // Jusqu'au dimanche soir de la semaine en cours. Un lundi, « cette semaine »
  // couvre donc six jours ; un samedi, un seul. C'est bien ce qu'on veut dire.
  const jsem = aujourdhui.getDay();
  const versDimanche = jsem === 0 ? 0 : 7 - jsem;
  const dimanche = new Date(
    aujourdhui.getFullYear(),
    aujourdhui.getMonth(),
    aujourdhui.getDate() + versDimanche,
  );
  return jour <= cleJour(dimanche);
}

/** Dans quelle tranche une tâche tombe-t-elle, à l'instant où on la regarde ? */
export function trancheDe(tache: Tache, maintenant: Date = new Date()): Tranche {
  if (tache.statut === 'fait') return 'fait';
  if (tache.echeance === null) return 'sans_echeance';

  const d = new Date(tache.echeance);
  if (Number.isNaN(d.getTime())) return 'sans_echeance';

  const jour = cleJour(d);
  const aujourdhui = cleJour(maintenant);
  if (jour < aujourdhui) return 'en_retard';
  if (jour === aujourdhui) return 'aujourdhui';

  const demain = new Date(
    maintenant.getFullYear(),
    maintenant.getMonth(),
    maintenant.getDate() + 1,
  );
  if (jour === cleJour(demain)) return 'demain';
  return dansLaSemaine(jour, maintenant) ? 'cette_semaine' : 'plus_tard';
}

/**
 * Le tri à l'intérieur d'une tranche : l'échéance d'abord, la priorité
 * ensuite, l'ancienneté pour départager.
 *
 * La priorité ne passe pas devant l'échéance : une tâche « basse » due demain
 * arrive avant une « urgente » due la semaine prochaine, et c'est normal —
 * dans une même tranche, ce qui presse est ce qui arrive en premier.
 */
export function comparerTaches(a: Tache, b: Tache): number {
  const ea = a.echeance ?? '';
  const eb = b.echeance ?? '';
  if (ea !== eb) {
    if (ea === '') return 1;
    if (eb === '') return -1;
    return ea < eb ? -1 : 1;
  }
  const pa = POIDS_PRIORITE[a.priorite];
  const pb = POIDS_PRIORITE[b.priorite];
  if (pa !== pb) return pa - pb;
  return a.createdAt.localeCompare(b.createdAt);
}

export interface GroupeTaches {
  tranche: Tranche;
  taches: Tache[];
}

/**
 * Les tâches rangées par tranche, dans l'ordre d'affichage.
 *
 * Les tranches vides ne sont pas rendues : une liste qui affiche « Demain
 * (0) » six fois de suite n'apprend rien. Les tâches faites arrivent en
 * dernier, de la plus récemment terminée à la plus ancienne.
 */
export function grouperTaches(
  taches: readonly Tache[],
  maintenant: Date = new Date(),
): GroupeTaches[] {
  const par = new Map<Tranche, Tache[]>();
  for (const t of taches) {
    const tr = trancheDe(t, maintenant);
    par.set(tr, [...(par.get(tr) ?? []), t]);
  }

  const groupes: GroupeTaches[] = [];
  for (const tranche of ORDRE_TRANCHES) {
    const liste = par.get(tranche);
    if (liste === undefined || liste.length === 0) continue;
    groupes.push({
      tranche,
      taches:
        tranche === 'fait'
          ? [...liste].sort((a, b) => (b.completeAt ?? '').localeCompare(a.completeAt ?? ''))
          : [...liste].sort(comparerTaches),
    });
  }
  return groupes;
}

export interface CompteursTaches {
  ouvertes: number;
  enRetard: number;
  aujourdhui: number;
  faites: number;
}

export function compterTaches(
  taches: readonly Tache[],
  maintenant: Date = new Date(),
): CompteursTaches {
  const c: CompteursTaches = { ouvertes: 0, enRetard: 0, aujourdhui: 0, faites: 0 };
  for (const t of taches) {
    const tr = trancheDe(t, maintenant);
    if (tr === 'fait') {
      c.faites += 1;
      continue;
    }
    c.ouvertes += 1;
    if (tr === 'en_retard') c.enRetard += 1;
    if (tr === 'aujourdhui') c.aujourdhui += 1;
  }
  return c;
}

/** Ce qu'il faut montrer en premier quand la place manque : le retard et le jour même. */
export function tachesDuJour(
  taches: readonly Tache[],
  maintenant: Date = new Date(),
  limite = 5,
): Tache[] {
  return taches
    .filter((t) => {
      const tr = trancheDe(t, maintenant);
      return tr === 'en_retard' || tr === 'aujourdhui';
    })
    .sort(comparerTaches)
    .slice(0, limite);
}

/** Lit une valeur venue de la base, qui n'est qu'une chaîne de caractères. */
export function statutValide(valeur: string): StatutTache {
  return (STATUTS as readonly string[]).includes(valeur) ? (valeur as StatutTache) : 'a_faire';
}

export function prioriteValide(valeur: string): PrioriteTache {
  return (PRIORITES as readonly string[]).includes(valeur) ? (valeur as PrioriteTache) : 'normale';
}
